import { v4 as uuidv4 } from 'uuid';

import config from 'config';
import type { Language } from 'services/i18n';
import { cookies, getLanguage } from 'services/i18n/utils';

import { ONE_TRUST_CATEGORY } from './oneTrustClient';

const LEGACY_COOKIE = 'gp_consent_privacy';
const OPTANON_CONSENT = 'OptanonConsent';
const OPTANON_ALERT_BOX_CLOSED = 'OptanonAlertBoxClosed';
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

/** Shape of each per-language entry in the legacy `gp_consent_privacy` cookie. */
export interface LegacyConsent {
  marketing: boolean;
  analytics: boolean;
  functional: boolean;
  essential: boolean;
}

export type LegacyConsentCookie = Record<string, LegacyConsent>;

/**
 * Maps the app language to the banner-language key the legacy
 * cookies-consent-manager used as the `gp_consent_privacy` cookie key.
 *
 * Copied (not imported) from the legacy provider on purpose: that provider is
 * removed during decommission (PAREXP-2106) but this migration must keep
 * reading old cookies for returning Partners afterward.
 */
const BANNER_LANGUAGE_KEY: Record<Language, string> = {
  it: 'it-it',
  'es-MX': 'es-mx',
  es: 'es-es',
  'es-AR': 'es-ar',
  'es-CL': 'es-cl',
  de: 'de-de',
  'en-GB': 'en-uk',
  en: 'en-us',
  'en-US': 'en-us',
  'en-IE': 'en-ie',
  'pt-BR': 'pt-br',
  pt: 'pt-br',
  'fr-CA': 'fr-ca',
  fr: 'fr-fr',
  'en-CA': 'en-ca',
  'es-ES': 'es-es',
  'pt-PT': 'pt-pt',
  ro: 'ro-ro',
  'de-AT': 'de-at',
  'de-CH': 'de-ch',
  'fr-CH': 'fr-ch',
  'nl-NL': 'nl-nl',
  'fr-LU': 'fr-lu',
  'de-LU': 'de-lu',
  'fr-FR': 'fr-fr',
  'nl-BE': 'nl-be',
  'fr-BE': 'fr-be',
};

/**
 * Selects the legacy consent entry for the given locale. Returns `null` when
 * the cookie is absent or has no entry for that locale — in which case the
 * caller must fall through to showing the OneTrust banner rather than seeding a
 * wrong default.
 */
export function selectLegacyConsent(
  cookie: LegacyConsentCookie | undefined,
  language: Language,
): LegacyConsent | null {
  if (!cookie) {
    return null;
  }

  const key = BANNER_LANGUAGE_KEY[language];

  return cookie[key] ?? null;
}

/**
 * Serializes consent into the OneTrust `OptanonConsent` groups string, e.g.
 * `C0001:1,C0004:0,C0002:0,C0003:0`. Essential (C0001) is always granted. The
 * order mirrors a real OptanonConsent cookie.
 */
export function serializeOptanonGroups(consent: LegacyConsent): string {
  const bit = (granted: boolean) => (granted ? '1' : '0');

  return [
    `${ONE_TRUST_CATEGORY.essential}:1`,
    `${ONE_TRUST_CATEGORY.marketing}:${bit(consent.marketing)}`,
    `${ONE_TRUST_CATEGORY.analytics}:${bit(consent.analytics)}`,
    `${ONE_TRUST_CATEGORY.functional}:${bit(consent.functional)}`,
  ].join(',');
}

function buildOptanonConsent(
  consent: LegacyConsent,
  timestamp: number,
): string {
  return [
    'isGpcEnabled=0',
    `datestamp=${timestamp}`,
    'version=202404.1.0',
    'isIABGlobal=false',
    `consentId=${uuidv4()}`,
    'interactionCount=1',
    `groups=${serializeOptanonGroups(consent)}`,
  ].join('&');
}

interface MigrationOptions {
  language?: Language;
  now?: Date;
}

/**
 * Seeds OneTrust cookies from the legacy `gp_consent_privacy` preference for the
 * current locale, so a returning Partner who already consented is not
 * re-prompted (no banner, no flash). Run this synchronously *before* the
 * OneTrust stub initializes.
 *
 * - Picks the entry for the current locale; if absent, does nothing and the
 *   banner is shown.
 * - Never overrides an existing OneTrust decision (`OptanonAlertBoxClosed`).
 * - Never erases the legacy cookie (other apps still read it).
 *
 * Returns `true` when consent was migrated, `false` otherwise.
 */
export function migrateLegacyConsent(options: MigrationOptions = {}): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  if (cookies.get(OPTANON_ALERT_BOX_CLOSED)) {
    return false;
  }

  const legacy = cookies.get(LEGACY_COOKIE) as LegacyConsentCookie | undefined;
  const language = options.language ?? getLanguage();
  const consent = selectLegacyConsent(legacy, language);

  if (!consent) {
    return false;
  }

  const now = options.now ?? new Date();
  // Seed on the parent domain (matching the app's other cookies and the legacy
  // gp_consent_privacy scope) so OneTrust — which manages OptanonConsent on the
  // registrable domain — recognizes the seeded consent and does not re-prompt
  // migrated Partners. Without this the cookies are host-only and OneTrust
  // shows the banner again.
  const cookieOptions = {
    path: '/',
    maxAge: ONE_YEAR_SECONDS,
    domain: config.PARTNERS_DOMAIN,
  } as const;

  cookies.set(
    OPTANON_CONSENT,
    buildOptanonConsent(consent, now.getTime()),
    cookieOptions,
  );
  cookies.set(OPTANON_ALERT_BOX_CLOSED, now.toISOString(), cookieOptions);

  return true;
}
